import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Avatar } from '@/components/Chips'
import { Dialog, Field, FIELD_CLASS } from '@/components/Dialog'
import { useSession } from '@/features/auth/useSession'
import {
  fetchPeople,
  fetchPerson,
  setPersonActive,
  updatePerson,
  type NotificationPreferences,
  type Person,
  type PersonProfile,
} from '@/features/people/api'
import { fetchDivisions } from '@/features/portfolio/api'
import type { ApiError } from '@/lib/api/client'
import type { Division } from '@/types/domain'

interface PersonForm {
  jobTitle: string
  divisionId: string
  managerId: string
  startedOn: string
  endedOn: string
  preferences: NotificationPreferences
}

const DEFAULT_PREFERENCES: NotificationPreferences = {
  dailyBrief: true,
  mentions: true,
  assignments: true,
  weeklyDigest: false,
  channel: 'email',
}

/**
 * Where a person sits in the group.
 *
 * The server splits this from the preferences someone sets about themselves —
 * job title, division, manager and dates all need `administer`, so a refusal
 * here is the policy working rather than a bug.
 *
 * Deactivating is the leaver flow: the records they made stay exactly where
 * they are and their access ends. It is not a delete, and the wording says so.
 */
export function PersonPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const session = useSession()
  const [form, setForm] = useState<PersonForm | null>(null)

  // PersonResource returns notificationPreferences only to the person
  // themselves — an administrator may end someone's access but not read their
  // inbox settings. The policy would let one *write* them, so showing the
  // section to anyone else would display defaults and save them over whatever
  // that person had actually chosen.
  const isSelf = session.data?.user.id === id

  const person = useQuery<PersonProfile, ApiError>({
    queryKey: ['person', id],
    queryFn: () => fetchPerson(id),
    retry: false,
  })

  const divisions = useQuery<Division[], ApiError>({
    queryKey: ['divisions'],
    queryFn: fetchDivisions,
    retry: false,
  })

  const people = useQuery<Person[], ApiError>({
    queryKey: ['people'],
    queryFn: fetchPeople,
    retry: false,
  })

  const data = person.data
  const values: PersonForm | null = form ?? (data === undefined ? null : {
    jobTitle: data.jobTitle ?? '',
    divisionId: data.divisionId ?? '',
    managerId: data.managerId ?? '',
    startedOn: data.startedOn ?? '',
    endedOn: data.endedOn ?? '',
    preferences: { ...DEFAULT_PREFERENCES, ...(data.notificationPreferences ?? {}) },
  })

  const set = (key: keyof PersonForm) => (value: string) => {
    if (values !== null) setForm({ ...values, [key]: value })
  }

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['person', id] })
    void queryClient.invalidateQueries({ queryKey: ['people'] })
    void queryClient.invalidateQueries({ queryKey: ['organization'] })
  }

  const blank = (v: string): string | null => (v.trim() === '' ? null : v.trim())

  const save = useMutation<PersonProfile, ApiError, void>({
    mutationFn: () =>
      updatePerson(id, {
        jobTitle: blank(values?.jobTitle ?? ''),
        divisionId: blank(values?.divisionId ?? ''),
        managerId: blank(values?.managerId ?? ''),
        startedOn: blank(values?.startedOn ?? ''),
        endedOn: blank(values?.endedOn ?? ''),
        ...(values !== null && isSelf ? { notificationPreferences: values.preferences } : {}),
      }),
    onSuccess: () => {
      refresh()
      onClose()
    },
  })

  const active = useMutation<PersonProfile, ApiError, boolean>({
    mutationFn: (next) => setPersonActive(id, next),
    onSuccess: refresh,
  })

  const error = save.error ?? active.error ?? person.error

  return (
    <Dialog title={data?.name ?? t('common.loading')} onClose={onClose}>
      {values === null || data === undefined ? (
        <p className="text-ink-500 px-5 py-4 font-mono text-[11px]">{t('common.loading')}</p>
      ) : (
        <form
          className="flex max-h-[70vh] flex-col gap-3.5 overflow-y-auto px-5 py-4"
          onSubmit={(event) => {
            event.preventDefault()
            save.mutate()
          }}
        >
          <div className="flex items-center gap-3">
            <Avatar name={data.name} color={data.color} />
            <div className="min-w-0 flex-1">
              <p className="text-ink-900 text-sm font-medium">{data.name}</p>
              <p className="text-ink-500 font-mono text-[11px] break-all">{data.email}</p>
            </div>
            {data.isActive ? null : (
              <span className="rounded px-1.5 py-px font-mono text-[9px] tracking-wide uppercase text-[color:var(--color-state-blocked)]">
                {t('organization.inactive')}
              </span>
            )}
          </div>

          <Field label={t('organization.jobTitle')}>
            <input value={values.jobTitle} onChange={(e) => set('jobTitle')(e.target.value)} className={FIELD_CLASS} />
          </Field>

          <div className="grid grid-cols-2 gap-3.5">
            <Field label={t('organization.division')}>
              <select value={values.divisionId} onChange={(e) => set('divisionId')(e.target.value)} className={FIELD_CLASS}>
                <option value="">{t('organization.none')}</option>
                {(divisions.data ?? []).map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </Field>

            <Field label={t('organization.manager')}>
              <select value={values.managerId} onChange={(e) => set('managerId')(e.target.value)} className={FIELD_CLASS}>
                <option value="">{t('organization.none')}</option>
                {(people.data ?? []).filter((p) => p.id !== id).map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </Field>

            <Field label={t('organization.startedOn')}>
              <input type="date" value={values.startedOn} onChange={(e) => set('startedOn')(e.target.value)} className={FIELD_CLASS} />
            </Field>

            <Field label={t('organization.endedOn')}>
              <input type="date" value={values.endedOn} onChange={(e) => set('endedOn')(e.target.value)} className={FIELD_CLASS} />
            </Field>
          </div>

          {!isSelf ? null : (
          <fieldset className="border-line flex flex-col gap-2 border-t pt-3.5">
            <legend className="text-ink-500 font-mono text-[10px] tracking-widest uppercase">
              {t('organization.notifications')}
            </legend>

            {([
              ['mentions', 'organization.prefMentions'],
              ['assignments', 'organization.prefAssignments'],
              ['dailyBrief', 'organization.prefDailyBrief'],
              ['weeklyDigest', 'organization.prefWeeklyDigest'],
            ] as const).map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 text-[13px]">
                <input
                  type="checkbox"
                  checked={values.preferences[key]}
                  disabled={values.preferences.channel === 'none'}
                  onChange={(event) =>
                    setForm({
                      ...values,
                      preferences: { ...values.preferences, [key]: event.target.checked },
                    })
                  }
                />
                <span className={values.preferences.channel === 'none' ? 'text-ink-500' : 'text-ink-700'}>
                  {t(label)}
                </span>
              </label>
            ))}

            <Field label={t('organization.prefChannel')}>
              <select
                value={values.preferences.channel}
                onChange={(event) =>
                  setForm({
                    ...values,
                    preferences: {
                      ...values.preferences,
                      channel: event.target.value as NotificationPreferences['channel'],
                    },
                  })
                }
                className={FIELD_CLASS}
              >
                <option value="email">{t('organization.channelEmail')}</option>
                <option value="in_app">{t('organization.channelInApp')}</option>
                <option value="none">{t('organization.channelNone')}</option>
              </select>
            </Field>

            <p className="text-ink-500 text-[11px] leading-tight">{t('organization.prefNote')}</p>
          </fieldset>
          )}

          <dl className="grid grid-cols-[110px_minmax(0,1fr)] gap-y-1.5 text-[12px]">
            <dt className="text-ink-500">{t('organization.openItems')}</dt>
            <dd className="text-ink-700 font-mono tabular-nums">{data.openWorkItems ?? 0}</dd>
            <dt className="text-ink-500">{t('organization.ledProjects')}</dt>
            <dd className="text-ink-700 font-mono tabular-nums">{data.ledProjectCount ?? 0}</dd>
            <dt className="text-ink-500">{t('organization.teams')}</dt>
            <dd className="text-ink-700">
              {data.teams.length === 0 ? t('organization.none') : data.teams.map((x) => x.name).join(' · ')}
            </dd>
          </dl>

          {error === null || error === undefined ? null : (
            <p className="text-[12px] text-[color:var(--color-state-blocked)]">{error.message}</p>
          )}

          <div className="border-line flex items-center gap-3 border-t pt-3.5">
            <button
              type="button"
              onClick={() => active.mutate(!data.isActive)}
              disabled={active.isPending}
              title={t('organization.deactivateNote')}
              className="text-ink-500 hover:text-[color:var(--color-state-blocked)] text-[12px] underline disabled:opacity-40"
            >
              {data.isActive ? t('organization.deactivate') : t('organization.reactivate')}
            </button>
            <button
              type="submit"
              disabled={save.isPending}
              className="bg-brand-600 ms-auto rounded px-3.5 py-1.5 text-[13px] font-medium text-white disabled:opacity-40"
            >
              {save.isPending ? t('organization.saving') : t('organization.save')}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  )
}
